import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Fingerprint, Microscope, Scale, FileSearch, Dna, FlaskConical, BookOpen, Building2 } from 'lucide-react';

const ForensicScience: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Institute of Forensic Sciences";
    
    return (
        <div className="min-h-screen bg-slate-50 font-sans text-slate-800">
            {/* Top Bar - University Style */}
            <div className="bg-[#0f172a] text-slate-300 py-3 px-8 text-xs font-medium border-b border-slate-800">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <span>A Department of the College of Criminal Justice</span>
                    <div className="flex gap-6">
                        <a href="#" className="hover:text-white transition-colors">Faculty Portal</a>
                        <a href="#" className="hover:text-white transition-colors">Lab Safety Protocols</a>
                        <a href="#" className="hover:text-white transition-colors">Alumni</a>
                    </div>
                </div>
            </div>

            {/* Navbar */}
            <nav className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-8 h-24 flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#0f172a] text-white flex items-center justify-center rounded-sm">
                            <Scale size={24} /> 
                        </div>
                        <div className="flex flex-col">
                            <span className="font-bold text-xl uppercase tracking-tight text-[#0f172a] leading-none">{schoolName}</span>
                            <span className="text-xs text-slate-500 font-medium tracking-widest uppercase mt-1">Science • Truth • Justice</span>
                        </div>
                    </div>

                    <div className="hidden md:flex items-center gap-8 font-bold text-sm text-slate-600 uppercase tracking-wide">
                        <a href="#" className="hover:text-[#0f172a] transition-colors">Academics</a>
                        <a href="#" className="hover:text-[#0f172a] transition-colors">Research Labs</a>
                        <a href="#" className="hover:text-[#0f172a] transition-colors">Faculty</a>
                        <a href="#" className="hover:text-[#0f172a] transition-colors">Admissions</a>
                    </div>

                    <div className="flex gap-4">
                        <Button className="bg-[#0f172a] hover:bg-slate-800 text-white font-bold uppercase tracking-wider text-xs px-6 py-6 rounded-sm">
                            Apply Now
                        </Button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="relative py-24 px-8 bg-white overflow-hidden">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
                    <div className="relative z-10">
                        <div className="inline-flex items-center gap-2 mb-6">
                             <div className="h-px w-8 bg-blue-600"></div>
                             <span className="text-blue-600 font-bold uppercase tracking-widest text-xs">Excellence in Criminalistics</span>
                        </div>
                        
                        <h1 className="text-5xl md:text-6xl font-bold text-[#0f172a] leading-tight mb-8">
                             Uncovering Truth Through <span className="text-blue-700">Scientific Inquiry.</span>
                        </h1>
                        
                        <p className="text-lg text-slate-600 mb-10 max-w-lg leading-relaxed border-l-4 border-slate-200 pl-6">
                            We train the next generation of forensic scientists to apply rigorous scientific methods to the administration of justice.
                        </p>
                        
                        <div className="flex gap-4">
                            <Button className="h-14 px-8 bg-blue-700 hover:bg-blue-800 text-white font-bold rounded-sm text-sm">
                                Explore Programs
                            </Button>
                            <Button variant="outline" className="h-14 px-8 border-slate-300 text-slate-700 hover:bg-slate-50 font-bold rounded-sm text-sm">
                                Virtual Lab Tour
                            </Button>
                        </div>
                    </div>

                    {/* Hero Image Composition */}
                    <div className="relative">
                         {/* Abstract "Lab" Visual */}
                         <div className="bg-slate-100 rounded-lg aspect-[4/3] relative overflow-hidden flex items-center justify-center border border-slate-200 shadow-xl">
                             <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1579165466741-7f35a4755657?q=80&w=2000&auto=format&fit=crop')] bg-cover bg-center opacity-80 grayscale mix-blend-multiply"></div>
                             <div className="bg-white/90 backdrop-blur-sm p-8 max-w-sm text-center shadow-lg relative z-10 border border-slate-200">
                                 <Microscope size={48} className="text-blue-700 mx-auto mb-4" />
                                 <h3 className="text-xl font-bold text-[#0f172a] mb-2">State-of-the-Art Facilities</h3>
                                 <p className="text-slate-500 text-sm">
                                     Our students train in 20,000 sq. ft. of dedicated laboratory space equipped with the latest instrumentation.
                                 </p>
                             </div>
                         </div>
                         {/* Decoration */}
                         <div className="absolute -bottom-6 -right-6 w-full h-full border-2 border-slate-200 -z-10 rounded-lg"></div>
                    </div>
                </div>
            </header>

            {/* Academic Departments */}
            <section className="py-24 px-8 bg-slate-100 border-t border-slate-200">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-[#0f172a] mb-4">Fields of Study</h2>
                        <div className="w-16 h-1 bg-blue-600 mx-auto"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            { icon: Dna, title: "Forensic Biology", desc: "DNA analysis, serology, and biological evidence examination." },
                            { icon: FlaskConical, title: "Forensic Chemistry", desc: "Analysis of controlled substances, toxicology, and trace evidence." },
                            { icon: Fingerprint, title: "Pattern Evidence", desc: "Latent prints, firearms, and toolmark identification." }
                        ].map((item, i) => (
                            <div key={i} className="bg-white p-8 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                                <item.icon size={32} className="text-blue-700 mb-6" />
                                <h3 className="text-xl font-bold text-[#0f172a] mb-3">{item.title}</h3>
                                <p className="text-slate-500 leading-relaxed mb-8 text-sm">{item.desc}</p>
                                <a href="#" className="text-blue-700 font-bold text-sm uppercase tracking-wider hover:underline">
                                    View Syllabus &rarr;
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

             {/* Footer */}
            <footer className="bg-[#0f172a] text-white py-20 px-8">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12 border-b border-slate-700 pb-12 mb-12">
                    <div className="col-span-1 md:col-span-2">
                        <div className="flex items-center gap-3 mb-6">
                            <Scale size={24} className="text-white" />
                            <span className="font-bold text-xl uppercase tracking-widest">{schoolName}</span>
                        </div>
                        <p className="text-slate-400 max-w-sm leading-relaxed">
                            Accredited by the Forensic Science Education Programs Accreditation Commission (FEPAC).
                        </p>
                    </div>
                    
                    <div>
                        <h4 className="font-bold mb-6 text-sm uppercase tracking-wider text-slate-400">Information For</h4>
                        <ul className="space-y-3 text-sm">
                            <li><a href="#" className="hover:text-blue-400 transition-colors">Prospective Students</a></li>
                            <li><a href="#" className="hover:text-blue-400 transition-colors">Current Students</a></li>
                            <li><a href="#" className="hover:text-blue-400 transition-colors">Law Enforcement</a></li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-bold mb-6 text-sm uppercase tracking-wider text-slate-400">Contact</h4>
                        <ul className="space-y-3 text-sm">
                            <li className="flex items-center gap-2"><Building2 size={16} /> Campus Access</li>
                            <li className="flex items-center gap-2"><FileSearch size={16} /> Department Directory</li>
                        </ul>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between text-xs text-slate-500">
                    <p>&copy; 2026 {schoolName}. All rights reserved.</p>
                    <div className="flex gap-6 mt-4 md:mt-0">
                        <a href="#" className="hover:text-white">Privacy Policy</a>
                        <a href="#" className="hover:text-white">Accessibility</a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default ForensicScience;
